import * as React from 'react';
import styled from 'styled-components';
import { $switchCaseStrict } from 'dolla-switch';
import { BaseButtonProps, ButtonSize, ButtonTheme } from './Button';
import { getTheme, ThemeName, disabledTheme } from './themes';

export type Shape = 'normal' | 'circle';

export type IconButtonSize = ButtonSize | 'jumbo';

type IconButtonProps = BaseButtonProps & {
    size?: IconButtonSize;
    shape?: Shape;
};

const IconButtonContainer = styled.a<{
    size: IconButtonSize;
    theme: ButtonTheme;
    shape: Shape;
    border: boolean;
    isActive?: boolean;
    disabled?: boolean;
}>`
    display: flex;
    justify-content: center;
    align-items: center;
    color: ${props => props.theme.fgColor};
    ${({ shape }) => (shape === 'circle' ? 'border-radius: 50%' : '')};
    background-color: ${props => props.theme.bgColor};
    border: 1px solid ${({ border, theme }) => (border ? theme.borderColor || theme.bgHoverColor : theme.bgColor)};
    outline: none;
    padding: 0;
    cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
    transition: background-color 0.2s ease, border-color 0.2s ease;

    ${({ size }) =>
        $switchCaseStrict(size, {
            small: () => `
                width: 24px;
                height: 24px;
            `,
            medium: () => `
                width: 32px;
                height: 32px;
            `,
            large: () => `
                width: 44px;
                height: 44px;
            `,
            jumbo: () => `
                width: 56px;
                height: 56px;
            `,
        })};

    &:hover {
        background-color: ${props => props.theme.bgHoverColor};
        border-color: ${({ theme }) => theme.borderHoverColor || theme.bgHoverColor};
    }
`;

export const IconButton: React.FunctionComponent<IconButtonProps> = ({
    target = '_self',
    ref,
    children,
    id,
    href,
    isActive,
    isDisabled = false,
    onClick,
    border = false,
    size = 'medium',
    theme = 'light',
    shape = 'normal',
    dataLookup,
}) => {
    const btnTheme = typeof theme === 'string' ? getTheme(theme as ThemeName) : theme;

    return (
        <IconButtonContainer
            id={id}
            ref={ref}
            size={size}
            className="btn"
            href={href}
            border={border}
            theme={isDisabled ? disabledTheme : btnTheme}
            shape={shape}
            target={href ? target : undefined}
            isActive={isActive}
            disabled={isDisabled}
            onClick={e => !isDisabled && onClick && onClick(e)}
            data-lookup={dataLookup || id}
        >
            {children}
        </IconButtonContainer>
    );
};
